/**
 * Zet het antwoord van de reno-mailendpoint om in het verzendrapport dat de gedeelde
 * SendResultsModal toont: naar wie er precies gemaild is (TO + CC + BCC), vanaf welk
 * adres, met welk onderwerp en of het gelukt is.
 */
export interface RenoSendResponse {
  success: boolean;
  /** Leeg wanneer de mail zonder zichtbare geadresseerde vertrok (alles in CC/BCC). */
  recipient: string;
  cc?: string[];
  bcc?: string[];
  sender?: string;
  subject?: string;
}

export interface RenoSendReport {
  totalSent: number;
  totalFailed: number;
  totalContacts: number;
  timestamp: string;
  senderEmail: string;
  subject: string;
  results: Array<{
    contactId: string;
    contactName: string;
    email: string;
    status: 'success' | 'failed';
    error?: string;
  }>;
  ccContacts?: Array<{ name: string; email: string }>;
  bccContacts?: Array<{ name: string; email: string }>;
}

/** Namen opzoeken bij een adres, zodat het rapport niet enkel e-mailadressen toont. */
type NameLookup = { name: string; email: string }[];

const nameFor = (email: string, lookup: NameLookup): string =>
  lookup.find((c) => c.email?.trim().toLowerCase() === email.toLowerCase())?.name || email;

export function buildRenoSendReport(
  response: RenoSendResponse,
  knownContacts: NameLookup = [],
  fallbackSubject = '',
): RenoSendReport {
  const succeeded = response.success !== false;
  const cc = (response.cc ?? []).filter(Boolean);
  const bcc = (response.bcc ?? []).filter(Boolean);

  // Zonder TO-ontvanger (alles in CC/BCC) zou één rij op `recipient` een leeg vakje
  // opleveren. Toon dan wie de mail wél gekregen heeft.
  const ontvangers = response.recipient ? [response.recipient] : [...cc, ...bcc];

  return {
    totalSent: succeeded ? ontvangers.length : 0,
    totalFailed: succeeded ? 0 : ontvangers.length,
    totalContacts: ontvangers.length,
    timestamp: new Date().toISOString(),
    senderEmail: response.sender || 'leo@yally.be',
    subject: response.subject || fallbackSubject,
    results: ontvangers.map((email) => ({
      contactId: email,
      contactName: nameFor(email, knownContacts),
      email,
      status: succeeded ? 'success' : 'failed',
    })),
    ccContacts: cc.length
      ? cc.map((email) => ({ name: nameFor(email, knownContacts), email }))
      : undefined,
    bccContacts: bcc.length
      ? bcc.map((email) => ({ name: nameFor(email, knownContacts), email }))
      : undefined,
  };
}
